import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Route, Plus, Pencil, Trash2, ShieldAlert, CheckCircle2, Loader2, GitBranch, Users, Phone, Mail, Bell, AlertTriangle } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", green: "#2F7D5A", amber: "#B7791F" };

const EMPTY = {
  name: "", description: "", priority: 10, enabled: true, branch_id: "",
  conditions: { event_type: "", service_lane: "", urgency: "" },
  actions: {
    assign_primary_user_id: "", assign_backup_user_id: "", notify_role_group: [],
    notify_central_admin: false, route_urgent_to_central: false, create_phone_task: false,
    escalate_if_no_response: false, create_in_app: true, queue_email: true,
  },
};

const NONE = "__none__";
const ANY = "__any__";

export default function SettingsRouting() {
  const [meta, setMeta] = useState(null);
  const [rules, setRules] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    try { const { data } = await api.get("/communications/routing-rules"); setRules(data.items || []); }
    catch (e) { toast.error(errMsg(e)); setRules([]); }
  }, []);
  const loadEsc = useCallback(async () => {
    try { const { data } = await api.get("/communications/escalations", { params: { status: "open" } }); setEscalations(data.items || []); }
    catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    api.get("/communications/routing-rules/meta").then(({ data }) => setMeta(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/staff").then(({ data }) => setStaff((data.items || data || []).map((u) => ({ id: u.id, name: u.full_name || u.email, role: u.role })))).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches((data.items || data || []).map((b) => ({ id: b.id, name: b.name || b.code || b.id })))).catch(() => {});
    loadRules(); loadEsc();
  }, [loadRules, loadEsc]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", description: r.description || "", priority: r.priority ?? 10,
      enabled: r.enabled !== false, branch_id: r.branch_id || "",
      conditions: {
        event_type: r.conditions?.event_type || "", service_lane: r.conditions?.service_lane || "",
        urgency: r.conditions?.urgency || "",
      },
      actions: { ...EMPTY.actions, ...(r.actions || {}), notify_role_group: r.actions?.notify_role_group || [] },
    });
    setDialog(true);
  };

  const setA = (k, v) => setForm((f) => ({ ...f, actions: { ...f.actions, [k]: v } }));
  const setCond = (k, v) => setForm((f) => ({ ...f, conditions: { ...f.conditions, [k]: v === ANY ? "" : v } }));
  const toggleRole = (role) => setForm((f) => {
    const cur = f.actions.notify_role_group || [];
    return { ...f, actions: { ...f.actions, notify_role_group: cur.includes(role) ? cur.filter((x) => x !== role) : [...cur, role] } };
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please give the rule a name."); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), description: form.description, priority: Number(form.priority) || 10,
      enabled: form.enabled, branch_id: form.branch_id || null,
      conditions: {
        event_type: form.conditions.event_type || null,
        service_lane: form.conditions.service_lane || null,
        urgency: form.conditions.urgency || null,
      },
      actions: {
        ...form.actions,
        assign_primary_user_id: form.actions.assign_primary_user_id || null,
        assign_backup_user_id: form.actions.assign_backup_user_id || null,
      },
    };
    try {
      if (editing) await api.put(`/communications/routing-rules/${editing.id}`, payload);
      else await api.post("/communications/routing-rules", payload);
      toast.success(editing ? "Rule updated." : "Rule created.");
      setDialog(false); loadRules();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const toggleEnabled = async (r) => {
    try { await api.put(`/communications/routing-rules/${r.id}`, { enabled: !r.enabled }); loadRules(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await api.delete(`/communications/routing-rules/${r.id}`); toast.success("Rule deleted."); loadRules(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const resolveEsc = async (e) => {
    try { await api.post(`/communications/escalations/${e.id}/resolve`); toast.success("Escalation resolved."); loadEsc(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const [slaBusy, setSlaBusy] = useState(false);
  const runSla = async () => {
    setSlaBusy(true);
    try { const { data } = await api.post("/communications/sla/run"); toast.success(`SLA check complete — ${data.escalated} item(s) escalated.`); loadEsc(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSlaBusy(false); }
  };

  const roleLabel = (v) => (meta?.roles || []).find((r) => r.value === v)?.label || v;
  const staffName = (id) => staff.find((s) => s.id === id)?.name || "—";

  if (!meta || rules === null) return <div className="max-w-4xl mx-auto space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-48" /></div>;

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="routing-settings-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
          <Route size={22} style={{ color: C.navy }} /> Notification Routing
        </h1>
        <Button onClick={openCreate} data-testid="routing-new-btn" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white"><Plus size={15} /> New rule</Button>
      </div>
      <p className="text-[13px] mb-5" style={{ color: C.slate }}>
        Route leads, chats, tasks, documents &amp; escalations to the right person: primary → backup → role group → central-admin fallback.
      </p>

      {/* Open escalations */}
      <Card className="p-4 border-[#E6EDF2] mb-5" data-testid="escalations-panel">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert size={16} style={{ color: C.red }} />
          <span className="text-[13.5px] font-semibold" style={{ color: C.charcoal }}>Open escalations</span>
          <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: escalations.length ? "#FDECEC" : "#EEF2F5", color: escalations.length ? C.red : C.slate }} data-testid="escalations-count">{escalations.length}</span>
          <Button variant="outline" size="sm" onClick={runSla} disabled={slaBusy} data-testid="run-sla-btn" className="ml-auto h-8 gap-1 border-[#D6E0E7] text-[#1C4358]">
            {slaBusy ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />} Run SLA check
          </Button>
        </div>
        {escalations.length === 0 ? (
          <div className="text-[12.5px] flex items-center gap-1.5 py-2" style={{ color: C.slate }}><CheckCircle2 size={14} style={{ color: C.green }} /> No open escalations — everything routed successfully.</div>
        ) : (
          <div className="space-y-2">
            {escalations.map((e) => (
              <div key={e.id} data-testid="escalation-row" className="flex items-start justify-between gap-3 rounded-lg border p-2.5" style={{ borderColor: C.line }}>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium flex items-center gap-1.5" style={{ color: C.charcoal }}>
                    <AlertTriangle size={13} style={{ color: C.amber }} /> {e.reason}
                    {e.used_central_fallback && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>central fallback</span>}
                  </div>
                  <div className="text-[11.5px]" style={{ color: C.slate }}>
                    {(e.event_type || "event").replace(/_/g, " ")} · priority {e.priority} · routed via {(e.routing_path || []).join(" → ") || "—"} · {(e.recipient_user_ids || []).length} recipient(s)
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => resolveEsc(e)} data-testid="escalation-resolve-btn" className="h-8 shrink-0 border-[#D6E0E7] text-[#1C4358]">Resolve</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Rules */}
      {rules.length === 0 ? (
        <div className="text-center py-14 rounded-xl border" style={{ borderColor: C.line }} data-testid="routing-empty">
          <Route size={26} className="mx-auto mb-2 text-[#9BB9CE]" />
          <p className="text-sm mb-3" style={{ color: C.slate }}>No routing rules yet. Unmatched items route to your default recipients and the central-admin fallback.</p>
          <Button onClick={openCreate} variant="outline" className="border-[#D6E0E7] text-[#1C4358]"><Plus size={15} className="mr-1" /> Create your first rule</Button>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="routing-rules-list">
          {rules.map((r) => (
            <Card key={r.id} className="p-4 border-[#E6EDF2]" data-testid="routing-rule-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-semibold" style={{ color: C.charcoal }}>{r.name}</span>
                    <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#EEF2F5", color: C.slate }}>priority {r.priority}</span>
                    {r.branch_id ? <span className="text-[10.5px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "#EEF2F5", color: C.navy }}><GitBranch size={10} /> branch</span>
                      : <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(28,67,88,0.08)", color: C.navy }}>global</span>}
                  </div>
                  {r.description && <div className="text-[12px] mt-0.5" style={{ color: C.slate }}>{r.description}</div>}
                  <div className="text-[11.5px] mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5" style={{ color: C.slate }}>
                    <span><b>When:</b> {[r.conditions?.event_type && (r.conditions.event_type).replace(/_/g, " "), r.conditions?.service_lane, r.conditions?.urgency && `${r.conditions.urgency} urgency`].filter(Boolean).join(" · ") || "any event"}</span>
                  </div>
                  <div className="text-[11.5px] mt-0.5 flex flex-wrap gap-1.5" style={{ color: C.slate }}>
                    {r.actions?.assign_primary_user_id && <Chip icon={Users}>Primary: {staffName(r.actions.assign_primary_user_id)}</Chip>}
                    {r.actions?.assign_backup_user_id && <Chip icon={Users}>Backup: {staffName(r.actions.assign_backup_user_id)}</Chip>}
                    {(r.actions?.notify_role_group || []).length > 0 && <Chip icon={Users}>{r.actions.notify_role_group.map(roleLabel).join(", ")}</Chip>}
                    {r.actions?.notify_central_admin && <Chip>Central admin</Chip>}
                    {r.actions?.route_urgent_to_central && <Chip>Urgent → central</Chip>}
                    {r.actions?.create_phone_task && <Chip icon={Phone}>Phone task</Chip>}
                    {r.actions?.create_in_app && <Chip icon={Bell}>In-app</Chip>}
                    {r.actions?.queue_email && <Chip icon={Mail}>Email</Chip>}
                    {r.actions?.escalate_if_no_response && <Chip icon={ShieldAlert}>Escalate</Chip>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={r.enabled !== false} onCheckedChange={() => r.can_manage && toggleEnabled(r)} disabled={!r.can_manage} data-testid="routing-rule-toggle" />
                  {r.can_manage && <>
                    <button onClick={() => openEdit(r)} data-testid="routing-rule-edit" className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]"><Pencil size={15} /></button>
                    <button onClick={() => del(r)} data-testid="routing-rule-delete" className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[rgba(156,30,34,0.08)] text-[#9C1E22]"><Trash2 size={15} /></button>
                  </>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="routing-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit routing rule" : "New routing rule"}</DialogTitle>
            <DialogDescription>Decide who is notified when an event matches these conditions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px]" style={{ color: C.slate }}>Rule name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Urgent KC leads to branch manager" className="mt-1 h-9 text-[13px]" data-testid="routing-field-name" />
            </div>
            <div>
              <Label className="text-[12px]" style={{ color: C.slate }}>Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="mt-1 text-[13px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]" style={{ color: C.slate }}>Priority (higher wins)</Label>
                <Input type="number" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="mt-1 h-9 text-[13px]" data-testid="routing-field-priority" />
              </div>
              {meta.is_super && (
                <div>
                  <Label className="text-[12px]" style={{ color: C.slate }}>Applies to</Label>
                  <Select value={form.branch_id || ANY} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v === ANY ? "" : v }))}>
                    <SelectTrigger className="mt-1 h-9 text-[13px]" data-testid="routing-field-branch"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY}>All branches (global)</SelectItem>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
              <div className="text-[12px] font-semibold mb-2" style={{ color: C.navy }}>When (conditions)</div>
              <div className="grid grid-cols-1 gap-2">
                <LabeledSelect label="Event type" value={form.conditions.event_type || ANY} onChange={(v) => setCond("event_type", v)}
                  options={[{ v: ANY, l: "Any event" }, ...(meta.event_types || []).map((t) => ({ v: t, l: t.replace(/_/g, " ") }))]} testid="routing-cond-event" />
                <LabeledSelect label="Service lane" value={form.conditions.service_lane || ANY} onChange={(v) => setCond("service_lane", v)}
                  options={[{ v: ANY, l: "Any lane" }, ...(meta.service_lanes || []).map((t) => ({ v: t, l: t.replace(/_/g, " ") }))]} testid="routing-cond-lane" />
                <LabeledSelect label="Urgency" value={form.conditions.urgency || ANY} onChange={(v) => setCond("urgency", v)}
                  options={[{ v: ANY, l: "Any urgency" }, ...(meta.urgencies || []).map((t) => ({ v: t, l: t }))]} testid="routing-cond-urgency" />
              </div>
            </div>

            <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
              <div className="text-[12px] font-semibold mb-2" style={{ color: C.navy }}>Then (actions)</div>
              <div className="grid grid-cols-1 gap-2">
                <LabeledSelect label="Assign primary" value={form.actions.assign_primary_user_id || NONE} onChange={(v) => setA("assign_primary_user_id", v === NONE ? "" : v)}
                  options={[{ v: NONE, l: "Use lead/record assignee" }, ...staff.map((s) => ({ v: s.id, l: s.name }))]} testid="routing-action-primary" />
                <LabeledSelect label="Assign backup" value={form.actions.assign_backup_user_id || NONE} onChange={(v) => setA("assign_backup_user_id", v === NONE ? "" : v)}
                  options={[{ v: NONE, l: "None" }, ...staff.map((s) => ({ v: s.id, l: s.name }))]} testid="routing-action-backup" />
              </div>
              <div className="mt-2">
                <Label className="text-[12px]" style={{ color: C.slate }}>Notify role group</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="routing-action-roles">
                  {(meta.roles || []).map((r) => {
                    const on = (form.actions.notify_role_group || []).includes(r.value);
                    return (
                      <button key={r.value} onClick={() => toggleRole(r.value)} type="button"
                        className="text-[11px] px-2 py-1 rounded-full border transition-colors"
                        style={{ borderColor: on ? C.navy : C.line, background: on ? "rgba(28,67,88,0.08)" : "#fff", color: on ? C.navy : C.slate }}>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <ToggleRow label="Notify central admin" checked={form.actions.notify_central_admin} onChange={(v) => setA("notify_central_admin", v)} testid="routing-action-central" />
                <ToggleRow label="Route urgent to central admin" checked={form.actions.route_urgent_to_central} onChange={(v) => setA("route_urgent_to_central", v)} testid="routing-action-urgent-central" />
                <ToggleRow label="Create a phone-call task" checked={form.actions.create_phone_task} onChange={(v) => setA("create_phone_task", v)} testid="routing-action-phone" />
                <ToggleRow label="Escalate if no response" checked={form.actions.escalate_if_no_response} onChange={(v) => setA("escalate_if_no_response", v)} testid="routing-action-escalate" />
                <ToggleRow label="Create in-app notification" checked={form.actions.create_in_app} onChange={(v) => setA("create_in_app", v)} testid="routing-action-inapp" />
                <ToggleRow label="Queue email" checked={form.actions.queue_email} onChange={(v) => setA("queue_email", v)} testid="routing-action-email" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-[13px] font-medium" style={{ color: C.charcoal }}>Rule enabled</Label>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="routing-field-enabled" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-[#D6E0E7]">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="routing-save-btn" className="gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {editing ? "Save rule" : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#EEF2F5", color: "#4B5563" }}>
    {Icon && <Icon size={10} />} {children}
  </span>
);

const LabeledSelect = ({ label, value, onChange, options, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <Label className="text-[12px] shrink-0" style={{ color: "#4B5563" }}>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[220px] text-[13px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between py-0.5">
    <span className="text-[12.5px]" style={{ color: "#252221" }}>{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);
